import { useEffect, useState } from 'react';

import { haversineMeters, type LatLng, round4 } from './geo';
import { MAPBOX_TOKEN } from './static-map-url';

// One Mapbox Directions (walking) call per (unit, applicant fix) pair. The
// straight-line distance renders instantly from geo.ts; this hook only adds the
// "≈ 35 min walk" suffix and must never block or spin.

export type WalkState =
	| { status: 'idle' }
	| { status: 'loading' }
	| { status: 'ready'; seconds: number; meters: number }
	| { status: 'none' };

export const ROUTE_DEBOUNCE_MS = 600;
// A walking route shorter than half, or longer than three times, the straight
// line is a routing artefact (snapped to the wrong road, wrong island…) and is
// withheld rather than shown.
export const PLAUSIBLE_MIN_RATIO = 0.5;
export const PLAUSIBLE_MAX_RATIO = 3;

const DIRECTIONS_BASE = 'https://api.mapbox.com/directions/v5/mapbox/walking';

type RouteResult = Extract<WalkState, { status: 'ready' | 'none' }>;

const routeCache = new Map<string, RouteResult>();

/** Test hook: forget every cached route. */
export const __clearRouteCache = (): void => {
	routeCache.clear();
};

export const routeCacheKey = (puId: string | number, from: LatLng): string => `${puId}:${round4(from.lat)},${round4(from.lng)}`;

export const directionsUrl = (from: LatLng, to: LatLng, token: string): string =>
	`${DIRECTIONS_BASE}/${from.lng.toFixed(6)},${from.lat.toFixed(6)};${to.lng.toFixed(6)},${to.lat.toFixed(6)}?overview=false&access_token=${encodeURIComponent(token)}`;

interface DirectionsRoute {
	duration?: unknown;
	distance?: unknown;
}

interface DirectionsBody {
	code?: unknown;
	routes?: unknown;
}

const asFinite = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Normalises a Directions response into a route result. Anything other than a
 * 200 with `code: "Ok"` and a plausible first route becomes `'none'`.
 */
export const interpretDirections = (ok: boolean, body: unknown, straightLineMeters: number): RouteResult => {
	if (!ok || typeof body !== 'object' || body === null) return { status: 'none' };
	const { code, routes } = body as DirectionsBody;
	if (code !== 'Ok' || !Array.isArray(routes) || routes.length === 0) return { status: 'none' };
	const first = routes[0] as DirectionsRoute;
	const seconds = asFinite(first.duration);
	const meters = asFinite(first.distance);
	if (seconds === null || meters === null || seconds <= 0 || meters <= 0) return { status: 'none' };
	if (straightLineMeters > 0) {
		const ratio = meters / straightLineMeters;
		if (ratio < PLAUSIBLE_MIN_RATIO || ratio > PLAUSIBLE_MAX_RATIO) return { status: 'none' };
	}
	return { status: 'ready', seconds, meters };
};

/**
 * @param puId  Stable id of the chosen unit (part of the cache key). `null` → idle.
 * @param from  The applicant's fix. `null` → idle.
 * @param to    The unit's coordinates. `null` → idle.
 */
export const useWalkingRoute = (puId: string | number | null, from: LatLng | null, to: LatLng | null): WalkState => {
	const [state, setState] = useState<WalkState>({ status: 'idle' });

	const key = puId !== null && from ? routeCacheKey(puId, from) : null;
	const toLat = to?.lat ?? null;
	const toLng = to?.lng ?? null;
	const fromLat = from?.lat ?? null;
	const fromLng = from?.lng ?? null;

	useEffect(() => {
		if (key === null || toLat === null || toLng === null || fromLat === null || fromLng === null) {
			setState({ status: 'idle' });
			return;
		}
		if (!MAPBOX_TOKEN) {
			setState({ status: 'none' });
			return;
		}

		const cached = routeCache.get(key);
		if (cached) {
			setState(cached);
			return;
		}

		const token = MAPBOX_TOKEN;
		const origin = { lat: fromLat, lng: fromLng };
		const destination = { lat: toLat, lng: toLng };
		const straight = haversineMeters(origin, destination);
		const controller = new AbortController();
		let cancelled = false;

		setState({ status: 'loading' });

		const timer = window.setTimeout(() => {
			fetch(directionsUrl(origin, destination, token), { signal: controller.signal })
				.then(async (res) => {
					// Only a genuine answer is cached: a route, a 200 NoRoute, or an
					// implausible route. A 429 / 5xx / unparsable body resolves to
					// 'none' for this render but is retried on the next mount.
					if (!res.ok) return { result: { status: 'none' } as RouteResult, cacheable: false };
					let body: unknown = null;
					try {
						body = await res.json();
					} catch {
						return { result: { status: 'none' } as RouteResult, cacheable: false };
					}
					return { result: interpretDirections(true, body, straight), cacheable: true };
				})
				.then(({ result, cacheable }) => {
					if (cancelled) return;
					if (cacheable) routeCache.set(key, result);
					setState(result);
				})
				.catch((error: unknown) => {
					// An abort means the inputs changed or we unmounted — the newer
					// effect run owns the state now, so say nothing.
					if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) return;
					// Transport failure: show nothing now, but do not remember it.
					setState({ status: 'none' });
				});
		}, ROUTE_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [key, toLat, toLng, fromLat, fromLng]);

	return state;
};
